import { Component } from '@theme/component';
import { eventBus, BackInStockRequestedEvent, PdpSizeRequiredEvent } from '@theme/events';
import { CartActions, CartAddEvent } from '@theme/cart--state--actions';
import { cartStore } from '@theme/cart--state--store';
import { runEffects } from '@theme/effect-registry';

/**
 * Cart Action Add — behavioral web component.
 *
 * A transparent wrapper that adds "add to cart" behavior to any projected content.
 * The element itself acts as the interactive target (role="button", tabindex="0").
 * All visual presentation comes from nested blocks — this component renders NO HTML.
 *
 * Architecture (ngrx-style):
 * - Emits CartAddEvent (action) to request adding item to cart
 * - Calls runEffects() to invoke declarative effect block handlers (dispatch/success/error)
 * - Listens to cart:item-added and cart:add-failed (results) for state feedback
 * - Exposes state via `data-state` attribute: '' | 'loading' | 'success' | 'error'
 *
 * Stock enforcement:
 * - Queries cartStore.getVariantQuantity() against data-inventory-quantity before adding
 * - On 422 error (server stock rejection), keeps error state until morph resets component
 * - Cart store tracks per-variant quantities centrally for all action blocks
 *
 * @attr {string} data-selected-variant - Variant ID to add
 * @attr {string} data-product-id - Product ID for event metadata
 * @attr {string} data-product-url - Product URL for preview link
 * @attr {string} data-product-title - Product title for preview
 * @attr {string} data-product-image - Featured image URL for preview
 * @attr {boolean} data-unavailable - When present, product is unavailable (triggers back-in-stock)
 * @attr {string} data-inventory-quantity - Max stock when inventory is tracked with deny policy
 * @attr {string} data-action - Action identifier ("cart:add")
 *
 * @extends {Component}
 */
class CartActionAdd extends Component {
	/** @type {(() => void)[]} */
	#unsubscribes = [];

	/** @type {string|null} */
	#pendingVariantId = null;

	/** @type {boolean} */
	#isAdding = false;

	connectedCallback() {
		super.connectedCallback();
		this.#setupResultListeners();
		// PDP context self-init: default to idle state when mounted inside
		// <action-product-context>. The context may later transition us to
		// ready/notify via data-atc-state. Parser order means pdp--context's
		// own connectedCallback can fire before this ATC is in the DOM, so
		// we can't rely on the parent to seed the initial state.
		if (!this.dataset.atcState && this.closest('action-product-context')) {
			this.dataset.atcState = 'idle';
		}
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.#pendingVariantId = null;
		this.#isAdding = false;

		for (const unsub of this.#unsubscribes) {
			unsub();
		}
		this.#unsubscribes = [];
	}

	/**
	 * Click handler — bound via on:click="/handleClick" in Liquid template.
	 * @param {Event} event
	 */
	handleClick(event) {
		event.preventDefault();
		event.stopPropagation();
		this.#trigger();
	}

	/**
	 * Keyboard handler — Enter/Space on the custom element (role="button").
	 * Bound via on:keydown="/handleKeydown" in Liquid template.
	 * @param {KeyboardEvent} event
	 */
	handleKeydown(event) {
		if (event.key !== 'Enter' && event.key !== ' ') return;
		event.preventDefault();
		this.#trigger();
	}

	/**
	 * Core trigger logic — shared between click and keyboard.
	 */
	#trigger() {
		if (this.#isAdding) return;

		if (this.dataset.atcState === 'idle') {
			this.dispatchEvent(new PdpSizeRequiredEvent());
			return;
		}

		const variantId = this.dataset.selectedVariant;
		if (!variantId) return;

		// Unavailable product (or PDP notify state) → back-in-stock request
		if (this.hasAttribute('data-unavailable')) {
			this.#requestBackInStock(variantId);
			return;
		}

		// Source-of-truth guard: the data-unavailable attribute can desync from
		// data-selected-variant across Section Rendering API morphs. Re-check the
		// selected variant against the embedded variant JSON (PDP context) so a
		// sold-out variant can never be added even if the attribute was lost.
		if (this.#isVariantUnavailable(variantId)) {
			this.#requestBackInStock(variantId);
			return;
		}

		// Stock validation: check cart store quantity against inventory limit
		const maxQty = parseInt(this.dataset.inventoryQuantity, 10);
		if (!isNaN(maxQty) && cartStore.getVariantQuantity(variantId) >= maxQty) {
			this.dataset.state = 'error';

			// Still run effects — opens drawer with existing item at top
			runEffects(this, 'dispatch', {
				action: 'cart:add',
				payload: { variantId, quantity: 1, productId: this.dataset.productId },
				preview: this.#extractPreviewData(),
			});
			return;
		}

		this.#addToCart(variantId);
	}

	/**
	 * Emit CartAddEvent + run effect handlers.
	 * @param {string} variantId
	 */
	#addToCart(variantId) {
		this.#isAdding = true;
		this.#pendingVariantId = variantId;
		this.dataset.state = 'loading';

		const preview = this.#extractPreviewData();

		// 1. Emit action: request to add item
		eventBus.emit(
			new CartAddEvent({
				data: {
					variantId,
					quantity: 1,
					source: 'cart-action-add',
					productId: this.dataset.productId,
				},
			})
		);

		// 2. Run effect handlers (declarative markers inside this element)
		const detail = {
			action: 'cart:add',
			payload: { variantId, quantity: 1, productId: this.dataset.productId },
			preview,
		};
		runEffects(this, 'dispatch', detail);
	}

	/**
	 * Build preview data from data attributes and optional product card ancestor.
	 * @returns {{ title: string, price: string, imageSrc: string, productUrl: string, variantTitle: string }}
	 */
	#extractPreviewData() {
		// Try to read richer data from product-card ancestor (when inside a card)
		const card = this.closest('product-card');
		const price = card?.querySelector('.product-card__price--current')?.textContent?.trim() ?? '';
		const cardImg = card?.querySelector('.product-card__image');

		return {
			title: this.dataset.productTitle ?? '',
			price: price || this.dataset.productPrice || '',
			imageSrc: cardImg?.currentSrc || cardImg?.src || this.dataset.productImage || '',
			imageRatio: parseFloat(this.dataset.productImageRatio) || 0,
			productUrl: this.dataset.productUrl ?? '',
			variantTitle: this.dataset.variantTitle ?? '',
			color: this.dataset.productColor ?? '',
		};
	}

	/**
	 * Look up a variant in the source-of-truth JSON embedded by
	 * <action-product-context> (PDP). Returns null when no such JSON exists
	 * (e.g. product cards) so non-PDP usage is unaffected.
	 * @param {string} variantId
	 * @returns {Object|null}
	 */
	#findVariant(variantId) {
		const context = this.closest('action-product-context');
		const script = context?.querySelector('script[data-product-variants]');
		if (!script) return null;

		try {
			const variants = JSON.parse(script.textContent ?? '[]');
			return variants.find((v) => String(v.id) === String(variantId)) ?? null;
		} catch {
			return null;
		}
	}

	/**
	 * Check whether the selected variant is unavailable per the source-of-truth
	 * variant JSON. Only returns true when the catalog positively reports the
	 * variant unavailable.
	 * @param {string} variantId
	 * @returns {boolean}
	 */
	#isVariantUnavailable(variantId) {
		const variant = this.#findVariant(variantId);
		return variant ? variant.available === false : false;
	}

	/**
	 * Dispatch back-in-stock notification request.
	 * @param {string} variantId
	 */
	#requestBackInStock(variantId) {
		// 'Default Title' is Shopify's placeholder for option-less products —
		// not a real size/option, so keep the modal's variant line empty.
		const variantTitle = this.#findVariant(variantId)?.title ?? '';

		eventBus.emit(
			new BackInStockRequestedEvent({
				data: {
					productTitle: this.dataset.productTitle ?? '',
					productUrl: this.dataset.productUrl ?? '',
					productImage: this.dataset.productImage ?? '',
					productId: this.dataset.productId ?? '',
					productColor: this.dataset.productColor ?? '',
					variantId,
					variantTitle: variantTitle === 'Default Title' ? '' : variantTitle,
					triggerElement: this,
				},
			})
		);
	}

	/**
	 * Listen for cart result events to update state feedback.
	 */
	#setupResultListeners() {
		// Success
		this.#unsubscribes.push(
			eventBus.on(CartActions.itemAdded, (event) => {
				const { variantId } = event.detail.data;
				if (String(variantId) !== String(this.#pendingVariantId)) return;

				this.dataset.state = 'success';

				runEffects(this, 'success', { action: 'cart:item-added', payload: { variantId } });

				setTimeout(() => {
					this.dataset.state = '';
				}, 2000);

				this.#isAdding = false;
				this.#pendingVariantId = null;
			})
		);

		// Failure
		this.#unsubscribes.push(
			eventBus.on(CartActions.addFailed, (event) => {
				const { variantId, message, status } = event.detail.data;
				if (String(variantId) !== String(this.#pendingVariantId)) return;

				this.dataset.state = 'error';

				runEffects(this, 'error', { action: 'cart:add-failed', payload: { variantId, message } });

				// On 422 (stock error), keep error persistent — store refreshes
				// quantities and #trigger() will block subsequent attempts
				if (status !== 422) {
					setTimeout(() => {
						this.dataset.state = '';
					}, 3000);
				}

				this.#isAdding = false;
				this.#pendingVariantId = null;
			})
		);
	}
}

if (!customElements.get('cart-action-add')) {
	customElements.define('cart-action-add', CartActionAdd);
}
